import React, { Fragment, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import SouthRoundedIcon from "@mui/icons-material/SouthRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import { brand } from "../../themes/theme";
import CountUp from "../CountUp";
import { formatInt } from "../../utils/format";

/**
 * Embudo con fuga visible y auto-explicativo:
 * - Cada fase = barra centrada (ancho = retencion); detras, el "fantasma" del
 *   ancho de la fase anterior: los flancos que quedan al descubierto = fuga.
 * - La retencion % y el nº de pacientes van DENTRO de la barra.
 * - La caida va ENTRE las barras; los 2 puntos criticos en naranja.
 * - Con `fill`, reparte el alto del contenedor (para caber sin scroll).
 * - Una fila con `row.drillTo` es clicable: abre el sub-embudo de esa rama
 *   (via `onRowClick`), senalizado con una flecha al final de la barra.
 *
 * - Una fila con `row.noData` SI mantiene su recuento real (la poblacion
 *   sigue ahi, no desaparece): la barra se ve normal. Lo unico que falta es
 *   una tasa/reparto especifico para ESA transicion -> se marca con una nota
 *   neutra entre barras ("sin perdida adicional conocida"), no ocultando el
 *   numero que si tenemos.
 *
 * @param {Array}  data - { label, retentionPct, count, caidaPp, critical, note, drillTo, noData }
 * @param {number} barHeight
 * @param {boolean} fill - ocupar el 100% del alto del padre
 * @param {function} onRowClick - (row) => void, llamada si la fila tiene drillTo
 */
function FunnelChart({ data, barHeight = 40, fill = false, onRowClick, dropUnit = "pp", continuesLabel = "sin pérdida adicional conocida" }) {
    const [shown, setShown] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setShown(true));
        return () => cancelAnimationFrame(id);
    }, []);

    const stagePy = fill ? 0.15 : 0.7;

    return (
        <Box
            sx={{
                ...(fill && {
                    height: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                }),
            }}
        >
            {data.map((row, i) => {
                const prev = i === 0 ? row.retentionPct : data[i - 1].retentionPct;
                const ghostColor = brand.gray100;
                return (
                    <Fragment key={i}>
                        {row.caidaPp != null &&
                            (row.critical ? (
                                <CriticalDrop pp={row.caidaPp} note={row.note} compact={fill} unit={dropUnit} />
                            ) : (
                                <NormalDrop pp={row.caidaPp} note={row.note} compact={fill} unit={dropUnit} />
                            ))}
                        {row.caidaPp == null && row.noData && <ContinuesNote note={row.note} compact={fill} label={continuesLabel} />}

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "72px 1fr", sm: "140px 1fr" },
                                alignItems: "center",
                                columnGap: { xs: 1.5, sm: 3 },
                                py: stagePy,
                                ...(fill && { flex: "1 1 0", minHeight: 0 }),
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: fill ? 12 : 13,
                                    textAlign: "right",
                                    color: "text.primary",
                                    lineHeight: 1.25,
                                }}
                            >
                                {row.label}
                            </Typography>

                            <Box
                                sx={{
                                    position: "relative",
                                    height: fill ? "100%" : barHeight,
                                    minHeight: fill ? 22 : barHeight,
                                    maxHeight: fill ? barHeight + 8 : undefined,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        bottom: 0,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        width: `${prev}%`,
                                        minWidth: 48,
                                        borderRadius: "7px",
                                        bgcolor: ghostColor,
                                    }}
                                />
                                <Box
                                    component={row.drillTo ? "button" : "div"}
                                    type={row.drillTo ? "button" : undefined}
                                    onClick={row.drillTo ? () => onRowClick && onRowClick(row) : undefined}
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        width: shown ? `${row.retentionPct}%` : "0%",
                                        minWidth: shown ? 72 : 0,
                                        height: "100%",
                                        bgcolor: "primary.main",
                                        border: 0,
                                        borderRadius: "7px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 0.75,
                                        color: "#fff",
                                        overflow: "visible",
                                        whiteSpace: "nowrap",
                                        cursor: row.drillTo ? "pointer" : "default",
                                        transition: `width 0.9s cubic-bezier(.22,1,.36,1) ${i * 90}ms, min-width 0.9s ${i * 90}ms, opacity .15s`,
                                        ...(row.drillTo && { "&:hover": { opacity: 0.88 } }),
                                        ...(row.critical && {
                                            animation: "mentialCriticalBar 3s ease-in-out infinite",
                                        }),
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: fill ? 13 : 14,
                                            fontWeight: 500,
                                            opacity: shown ? 1 : 0,
                                            transition: `opacity 0.25s ease ${i * 90 + 350}ms`,
                                        }}
                                    >
                                        <CountUp to={row.retentionPct} suffix="%" duration={900} />
                                    </Box>
                                    {row.count != null && (
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: fill ? 11 : 11.5,
                                                opacity: shown ? 0.85 : 0,
                                                transition: `opacity 0.25s ease ${i * 90 + 350}ms`,
                                            }}
                                        >
                                            <CountUp to={row.count} format={formatInt} duration={900} />
                                        </Box>
                                    )}
                                    {row.drillTo && (
                                        <ChevronRightRoundedIcon
                                            sx={{ fontSize: fill ? 15 : 16, opacity: shown ? 0.85 : 0, transition: `opacity 0.25s ease ${i * 90 + 350}ms` }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Fragment>
                );
            })}
        </Box>
    );
}

function NormalDrop({ pp, note, compact, unit = "pp" }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "72px 1fr", sm: "140px 1fr" },
                columnGap: { xs: 1.5, sm: 3 },
                flexShrink: 0,
            }}
        >
            <span />
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 0.5,
                    py: compact ? 0.05 : 0.25,
                }}
            >
                <SouthRoundedIcon sx={{ fontSize: compact ? 11 : 12, color: "text.secondary" }} />
                <Typography sx={{ fontSize: compact ? 11 : 11.5, color: "text.secondary" }}>
                    -{pp}{unit === "pp" ? " pp" : "%"}{note ? ` · ${note}` : ""}
                </Typography>
            </Box>
        </Box>
    );
}

// Fila sin caida (row.noData): la poblacion sigue igual, no se pierde nada
// MEDIDO en esta transicion -- pero falta un dato puntual (p.ej. un reparto).
// Nota neutra, sin flecha de caida (no es una fuga), para no leerse como "-0 pp".
function ContinuesNote({ note, compact, label }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "72px 1fr", sm: "140px 1fr" },
                columnGap: { xs: 1.5, sm: 3 },
                flexShrink: 0,
            }}
        >
            <span />
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 0.5,
                    py: compact ? 0.05 : 0.25,
                }}
            >
                <Box sx={{ width: 10, height: 1.5, bgcolor: "text.secondary", opacity: 0.6 }} />
                <Typography sx={{ fontSize: compact ? 11 : 11.5, color: "text.secondary" }}>
                    {label}{note ? ` · ${note}` : ""}
                </Typography>
            </Box>
        </Box>
    );
}

function CriticalDrop({ pp, note, compact, unit = "pp" }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "72px 1fr", sm: "140px 1fr" },
                columnGap: { xs: 1.5, sm: 3 },
                flexShrink: 0,
            }}
        >
            <span />
            <Box sx={{ display: "flex", justifyContent: "center", py: compact ? 0.15 : 0.4 }}>
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: compact ? 1 : 1.25,
                        py: compact ? 0.2 : 0.4,
                        borderRadius: 999,
                        bgcolor: "rgba(255,95,36,0.12)",
                        color: "accent.main",
                        border: "1px solid",
                        borderColor: "rgba(255,95,36,0.45)",
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 14,
                            height: 14,
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                width: 14,
                                height: 14,
                                borderRadius: "50%",
                                border: "1.5px solid",
                                borderColor: "accent.main",
                                animation: "mentialPing 1.8s ease-out infinite",
                            }}
                        />
                        <WarningAmberRoundedIcon sx={{ fontSize: 13, position: "relative" }} />
                    </Box>
                    <Typography component="span" sx={{ fontSize: compact ? 12 : 13, fontWeight: 500, color: "accent.main" }}>
                        -{pp}{unit === "pp" ? " pp" : "%"}
                    </Typography>
                    {note && (
                        <Typography component="span" sx={{ fontSize: compact ? 12 : 13, color: "accent.main", opacity: 0.9 }}>
                            · {note}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default FunnelChart;
